// Canonical object references on the client (busy-timeline-srv#20, AI Brain spec §2).
//
// The services now speak one reference for everything — `{ type, id }`, owned by a named service —
// and the Work-board is the one place in the web app that holds a bag of THINGS from all over: a
// research pack, a picked email thread, a saved photo, a document. Until now each of those was just
// an item with a client-side string id ("pack:<guid>", "email:<guid>"), which no backend could
// resolve back to the object it stands for.
//
// So a board item that stands for a real, server-side object carries `objectRef` beside everything
// it already had. Purely additive: nothing reads it yet, no id changes, no behaviour moves. What it
// buys is that the next thing that wants to ask "what IS this thing on the board" (the resolver at
// GET v1/objects/{type}/{id}) has something to ask WITH.
//
// Pure module, no store and no Vue, so the rules below can be checked on their own.

/** The types busy-timeline-srv's ObjectType knows. Anything else is not a reference. */
export const OBJECT_TYPES = [
  'ongoing', 'timeline', 'task', 'event', 'note', 'document', 'contact', 'researchpack', 'chat',
]

/** Which repo owns each kind — the client half of the routing table, for display and logging. */
export const OBJECT_SERVICES = {
  ongoing: 'busy-timeline-srv',
  timeline: 'busy-timeline-srv',
  researchpack: 'busy-timeline-srv',
  task: 'busy-task-srv',
  note: 'busy-note-srv',
  event: 'clndr-clndr-calendar-srv',
  document: 'busy-document-srv',
  contact: 'acc-account-sync-srv',
  chat: 'busy-ai-srv',
}

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** "research-pack" / "ResearchPack" / "researchPack" all name the same type. */
function normaliseType(type) {
  if (!type) return null
  const t = String(type).replace(/[-_\s]/g, '').toLowerCase()
  return OBJECT_TYPES.includes(t) ? t : null
}

/**
 * A reference, or null. Null for a type nobody knows and for an id that is not a server id: guests'
 * drafts ("local-3"), research tiles ("turn-1-up-0") and the board's own composite keys
 * ("pack:<guid>") name nothing a service could resolve, and a reference that resolves to nothing is
 * worse than no reference — it invites a lookup that can only 404.
 */
export function objectRef(type, id) {
  const t = normaliseType(type)
  if (!t || !id || !GUID.test(String(id))) return null
  return { type: t, id: String(id) }
}

/** The wire form the services speak: "task:<guid>@busy-task-srv". Empty string for no reference. */
export function refString(ref) {
  if (!ref?.type || !ref?.id) return ''
  return `${ref.type}:${ref.id}@${OBJECT_SERVICES[ref.type] || ''}`
}

/**
 * The reference a Work-board item stands for, or null when it stands for nothing resolvable.
 *
 * Null is the common, correct answer: a saved photo, snippet or link is a RESEARCH ITEM, and a
 * research item has no object type of its own yet (busy-timeline-srv#20 left that open), while a
 * ticked sender group is a search, not a row. Both stay on the board exactly as before — they simply
 * do not claim to be something a resolver can be asked about.
 */
export function objectRefOf(item) {
  if (!item) return null
  // Something already said what this is — a caller that knows beats anything derived here.
  if (item.objectRef?.type && item.objectRef?.id) {
    return objectRef(item.objectRef.type, item.objectRef.id)
  }
  if (item.objectType) return objectRef(item.objectType, item.objectId ?? item.entityId ?? item.id)

  switch (item.kind) {
    // An opened Research Pack rides on the board as context; the pack itself is a real row.
    case 'pack':
      return objectRef('researchpack', item.packId)
    // A ticked email is a ticked THREAD: the workspace lists threads (timeline-search/listing), so
    // the pick's emailId is a timeline id — which is what the estate refers to an email by.
    case 'email-pick':
      return objectRef('timeline', item.emailId ?? item.timelineId)
    // A sender group / email connection is a rule about future mail, not a row: no id, no reference.
    case 'email-group':
    case 'email-connection':
      return null
    case 'doc':
    case 'document':
      return objectRef('document', item.documentId ?? item.entityId)
    default:
      // A row that already names an entity (the global timeline feed's task/note/event cards).
      return item.entityId ? objectRef(item.type ?? item.entityType, item.entityId) : null
  }
}

/**
 * The item as it goes onto the board: unchanged, plus `objectRef` when one can be derived. Items
 * that stand for nothing resolvable are returned untouched — no null field, so nothing that reads
 * the board sees a new key it has to think about.
 */
export function withObjectRef(item) {
  if (!item) return item
  if (item.objectRef?.type && item.objectRef?.id) return item
  const ref = objectRefOf(item)
  return ref ? { ...item, objectRef: ref } : item
}
